import Head from 'next/head'
import Link from 'next/link'
import { FC, PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import configuration from '~/configuration'
import { Button } from '~/core/ui/Button'
import { TypographyText } from '~/core/ui/Text'
import { cn } from '~/core/ui/utils'
import LPContainer from './LPContainer'

interface Props extends PropsWithChildren {
  badgeLabel: string
  badgeColor: string
  title: string
  description: string
  images: {
    desktop: string
    mobile: string
  }
  badge?: 'star' | 'rocket' | 'building'
  ctaLabel?: string
  className?: string
  contentClassName?: string
  imageClassName?: string
}

const HeroBanner: FC<Props> = ({
  badgeLabel,
  badgeColor,
  title,
  description,
  images,
  ctaLabel,
  className,
  imageClassName
}) => {
  const { t, i18n } = useTranslation()
  const ctaLabelValue = ctaLabel || t('lp:request_demo')

  return (
    <>
      <Head>
        <link rel="preload" as="image" href={images.mobile} />
        <link
          rel="preload"
          as="image"
          href="/img/feature/lp/hero-background.png"
        />
      </Head>

      <div
        className={cn(
          'bg-hero relative flex flex-col-reverse py-[64px] tablet:pt-[120px] tablet:pb-0',
          className
        )}>
        <LPContainer className="flex tablet:flex-row flex-col items-center relative max-w-[1266px]">
          <div className="desktop:mb-0 mb-8 tablet:mr-[30px] tablet:w-[600px]">
            <span
              className={cn('px-2 text-base uppercase rounded', badgeColor)}>
              {badgeLabel}
            </span>
            <TypographyText className="leading-[52px] font-medium text-gray-900 text-[43px] tablet:text-[61px] tablet:leading-[61px] text-center tablet:text-left animate-fadeInUp mt-6">
              {title}
            </TypographyText>
            <TypographyText className="mt-4 mb-8 text-gray-700 text-base font-normal text-center tablet:text-left animate-fadeInUp">
              {description}
            </TypographyText>
            <div className="flex justify-center tablet:justify-start animate-fadeInUp">
              <Link
                href={configuration.path.contactRequest}
                locale={i18n?.language}>
                <Button size="xl" label={ctaLabelValue} />
              </Link>
            </div>
          </div>
          <div
            className={cn(
              'tablet:flex-1 w-full flex items-center',
              imageClassName
            )}>
            <picture>
              <source media="(min-width: 768px)" srcSet={images.desktop} />
              <source media="(max-width: 767px)" srcSet={images.mobile} />
              <img alt={title} fetchPriority="high" />
            </picture>
          </div>
        </LPContainer>
      </div>
    </>
  )
}

export default HeroBanner
